<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header Principal Corporativo -->
    <v-sheet color="white" class="pa-6 border-bottom">
      <v-row align="center" justify="space-between" no-gutters>
        <v-col cols="12" md="6" class="d-flex align-center">
          <v-avatar size="48" color="primary lighten-5" class="mr-3 rounded-lg">
            <v-icon color="primary">fas fa-boxes</v-icon>
          </v-avatar>
          <div>
            <h1 class="text-h5 font-weight-black grey--text text--darken-4 tracking-tight mb-0">
              Dashboard de Inventarios
            </h1>
            <div class="caption font-weight-bold grey--text">
              Existencias, valorización y criticidad de stock
              <span v-if="asOfLabel">· al {{ asOfLabel }}</span>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-center justify-md-end mt-4 mt-md-0 flex-wrap" style="gap: 10px">
          <v-btn
            outlined
            color="grey darken-1"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            @click="$router.push({ name: 'Stock' })"
          >
            <v-icon left small>fas fa-warehouse</v-icon>
            Inventario
          </v-btn>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loading"
            @click="load(true)"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 pt-6">
      <!-- Barra de Filtros (Bodega, Familia, Rango de Días) -->
      <v-card flat class="panel-card mb-6 shadow-xs">
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <div class="caption font-weight-black text-uppercase grey--text mb-1 ml-1">Bodega</div>
            <v-select
              v-model="store"
              :items="storeOptions"
              item-text="name"
              item-value="id"
              placeholder="Todas las bodegas"
              outlined
              dense
              hide-details
              clearable
              class="compact-select"
              prepend-inner-icon="fas fa-warehouse"
            />
          </v-col>

          <v-col cols="12" md="4">
            <div class="caption font-weight-black text-uppercase grey--text mb-1 ml-1">Familia</div>
            <v-select
              v-model="family"
              :items="familyOptions"
              item-text="name"
              item-value="id"
              placeholder="Todas las familias"
              outlined
              dense
              hide-details
              clearable
              class="compact-select"
              prepend-inner-icon="fas fa-filter"
            />
          </v-col>

          <v-col cols="12" md="4">
            <div class="caption font-weight-black text-uppercase grey--text mb-1 ml-1">Movimiento</div>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <v-btn
                v-for="preset in dayPresets"
                :key="preset.value"
                x-small
                depressed
                class="rounded-pill font-weight-bold text-none"
                :color="days === preset.value ? 'primary' : 'grey lighten-4'"
                :class="days === preset.value ? 'white--text' : 'grey--text text--darken-2'"
                @click="days = preset.value"
              >
                {{ preset.label }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Estado de Error con Reintento -->
      <v-alert
        v-if="error"
        dense
        outlined
        type="error"
        class="mb-6 rounded-xl bg-white"
      >
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <span>{{ error }}</span>
          <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="load(true)">
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
          </v-btn>
        </div>
      </v-alert>

      <!-- Skeletons de Carga -->
      <v-row v-if="loading && !asOf" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Fila Héroe de 4 KPIs Maestros -->
        <v-row dense class="mb-6">
          <!-- KPI 1: Inventario Valorizado (North Star) -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100 is-primary">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Inventario Valorizado</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Valor total en moneda local del stock físico existente en bodegas filtradas.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value primary--text text--darken-3">
                  {{ kpis.value | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    {{ kpis.with_stock || 0 }} SKU con existencia activa
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 2: Artículos en Catálogo -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Artículos en Catálogo</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Total de SKU activos registrados en el maestro de inventario.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value grey--text text--darken-4">
                  {{ kpis.articles || 0 }} <span class="unit-text">SKU</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    {{ kpis.inactive || 0 }} inactivos · {{ kpis.qty | number('0,0') }} unidades
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 3: Stock Crítico -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100" :class="{ 'is-warning-card': (kpis.critical || 0) > 0 }">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Stock Crítico</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Artículos cuyo saldo actual está por debajo del umbral mínimo de seguridad.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value" :class="(kpis.critical || 0) > 0 ? 'error--text text--darken-2' : 'teal--text text--darken-3'">
                  {{ kpis.critical || 0 }} <span class="unit-text">SKU</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    {{ kpis.zero_stock || 0 }} artículos activos en cero
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 4: OC por Recepcionar -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">OC por Recepcionar</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Valor estimado de órdenes de compra aprobadas pendientes de ingreso a bodega.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value grey--text text--darken-4">
                  {{ kpis.pending_po_value | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    {{ kpis.pending_pos || 0 }} OC · {{ kpis.open_supplies || 0 }} mat. en OT
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Banner Ejecutivo de Lectura de Inventario (Actionable Executive Insight) -->
        <v-row v-if="insight" class="mb-6">
          <v-col cols="12">
            <v-card flat class="rounded-xl pa-5 shadow-xs insight-executive-card" :class="'is-' + (insight.severity || 'info')">
              <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px">
                <div class="d-flex align-center flex-wrap" style="gap: 10px">
                  <v-chip :color="insightChipColor" small class="font-weight-black text-uppercase tracking-widest px-3 white--text">
                    <v-icon left x-small color="white">{{ insightIcon }}</v-icon>
                    {{ insightChipLabel }}
                  </v-chip>
                  <h2 class="text-subtitle-1 font-weight-black mb-0 white--text">
                    {{ insight.title }}
                  </h2>
                </div>

                <div class="d-flex align-center flex-wrap" style="gap: 8px">
                  <v-btn
                    v-if="insight.action_label || insight.highlight"
                    depressed
                    class="rounded-pill font-weight-bold text-none px-5 shadow-xs"
                    :color="insightButtonColor"
                    @click="triggerInsightAction"
                  >
                    <v-icon left small>{{ insightActionIcon }}</v-icon>
                    {{ insight.action_label || insight.highlight }}
                  </v-btn>

                  <v-btn
                    v-if="insight.type === 'critical'"
                    text
                    small
                    class="rounded-pill font-weight-bold text-none grey--text text--lighten-2"
                    @click="$router.push({ name: 'Stock', query: { alert: 'true' } })"
                  >
                    Ver en Inventario General →
                  </v-btn>
                </div>
              </div>

              <div class="text-body-2 grey--text text--lighten-2 mt-3 mb-0" style="max-width: 920px; line-height: 1.5">
                {{ insight.body }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Balance de Flujo (Entradas, Salidas, Con Stock, Sin Stock) -->
        <v-row dense class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="panel-card shadow-xs pa-4 flow-card is-in">
              <div class="d-flex align-center justify-space-between">
                <div class="caption font-weight-black text-uppercase success--text text--darken-2">Entradas</div>
                <v-icon small color="success">fas fa-arrow-down</v-icon>
              </div>
              <div class="text-h5 font-weight-black success--text text--darken-3 mt-2">
                {{ movement.in_qty | number('0,0.0') }}
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
                {{ movement.in_count || 0 }} mov. · {{ movement.in_value | moneda }}
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card flat class="panel-card shadow-xs pa-4 flow-card is-out">
              <div class="d-flex align-center justify-space-between">
                <div class="caption font-weight-black text-uppercase error--text text--darken-2">Salidas</div>
                <v-icon small color="error">fas fa-arrow-up</v-icon>
              </div>
              <div class="text-h5 font-weight-black error--text text--darken-3 mt-2">
                {{ movement.out_qty | number('0,0.0') }}
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
                {{ movement.out_count || 0 }} mov. · {{ movement.out_value | moneda }}
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card flat class="panel-card shadow-xs pa-4 flow-card is-cyan">
              <div class="d-flex align-center justify-space-between">
                <div class="caption font-weight-black text-uppercase primary--text">Con Stock</div>
                <v-icon small color="primary">fas fa-check-circle</v-icon>
              </div>
              <div class="text-h5 font-weight-black primary--text mt-2">
                {{ kpis.with_stock || 0 }}
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
                de {{ kpis.articles || 0 }} SKU activos
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card flat class="panel-card shadow-xs pa-4 flow-card is-amber">
              <div class="d-flex align-center justify-space-between">
                <div class="caption font-weight-black text-uppercase warning--text text--darken-3">Sin Stock</div>
                <v-icon small color="warning">fas fa-exclamation-triangle</v-icon>
              </div>
              <div class="text-h5 font-weight-black warning--text text--darken-4 mt-2">
                {{ kpis.zero_stock || 0 }}
              </div>
              <div class="caption font-weight-bold grey--text text--darken-2 mt-1">
                artículos activos en cero
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Movimiento de Bodega & Tipos de Transacción -->
        <v-row dense class="mb-6">
          <v-col cols="12" lg="8">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Movimiento de Bodega</div>
                <span class="caption grey--text font-weight-bold">Últimos {{ days }} días</span>
              </div>
              <div class="panel-sub mb-4">Entradas vs salidas por día</div>

              <div v-if="!(daily && daily.length)" class="empty-mini py-12 text-center">Sin movimientos en el período</div>
              <div v-else class="day-strip pa-3 rounded-lg">
                <div
                  v-for="day in daily"
                  :key="day.date"
                  class="day-cell"
                  :class="{ 'is-empty': !day.in_qty && !day.out_qty }"
                >
                  <span class="day-name">{{ day.label }}</span>
                  <span class="day-bar-track">
                    <span class="day-bar in" :style="{ height: barHeight(day.in_qty) }"></span>
                    <span class="day-bar out" :style="{ height: barHeight(day.out_qty) }"></span>
                  </span>
                  <span class="day-count">{{ day.in_count + day.out_count || '—' }}</span>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Tipos de Transacción</div>
                <span class="caption grey--text font-weight-bold">{{ tipos.length }} tipos</span>
              </div>
              <div class="panel-sub mb-4">Volumen valorizado del período</div>

              <div v-if="!tipos.length" class="empty-mini py-6 text-center">Sin movimientos registrados</div>
              <div v-for="row in tipos" :key="row.name" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 60%">{{ row.name }}</div>
                  <div class="rank-val font-weight-bold">{{ row.value | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.count }} mov. · {{ row.qty | number('0,0.0') }} un</div>
                </div>
                <div class="rank-track">
                  <div class="rank-fill" :class="row.incoming ? 'is-teal' : ''" :style="{ width: rankWidth(row.value, maxTipoValue) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Distribución de Valor: Por Bodega y Por Familia -->
        <v-row dense class="mb-6">
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Valor por Bodega</div>
                <span class="caption grey--text font-weight-bold">{{ stores.length }} bodegas</span>
              </div>
              <div class="panel-sub mb-4">Distribución del capital físico almacenado</div>

              <div v-if="!stores.length" class="empty-mini py-6 text-center">Sin existencias</div>
              <div v-for="row in stores" :key="row.name" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-bold">{{ row.value | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.skus }} SKU · {{ row.qty | number('0,0.0') }} un</div>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-teal" :style="{ width: rankWidth(row.value, maxStoreValue) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Valor por Familia</div>
                <span class="caption grey--text font-weight-bold">{{ families.length }} familias</span>
              </div>
              <div class="panel-sub mb-4">Concentración del valor por categoría</div>

              <div v-if="!families.length" class="empty-mini py-6 text-center">Sin familias valorizadas</div>
              <div v-for="row in families" :key="row.name" class="rank-row">
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                  <div class="rank-val font-weight-bold">{{ row.value | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">{{ row.skus }} SKU · {{ row.qty | number('0,0.0') }} un</div>
                </div>
                <div class="rank-track">
                  <div class="rank-fill" :style="{ width: rankWidth(row.value, maxFamilyValue) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Listas Operativas: Stock Crítico & Mayor Capital Inmovilizado -->
        <v-row dense class="mb-6">
          <!-- Lista 1: Stock Crítico -->
          <v-col cols="12" md="6">
            <v-card ref="criticalCard" class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title error--text text--darken-2">
                  <v-icon x-small color="error" class="mr-1">fas fa-exclamation-circle</v-icon>
                  Stock Crítico
                </div>
                <span class="caption error--text font-weight-bold">Bajo el mínimo definido</span>
              </div>
              <div class="panel-sub mb-4">Haz clic sobre un SKU para auditar su ficha técnica</div>

              <div v-if="!critical.length" class="empty-mini py-6 text-center">Ningún SKU bajo crítico</div>
              <div
                v-for="row in critical"
                :key="'c-' + row.id"
                class="item-card-row mb-2 pa-3 rounded-lg is-clickable"
                @click="openArticle(row.id)"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="item-code font-weight-bold text-truncate error--text" style="max-width: 260px">
                      {{ row.sku || ('ID ' + row.id) }}
                    </div>
                    <div class="caption grey--text text-truncate" style="max-width: 260px">
                      {{ row.name }} · {{ row.family || 'Sin familia' }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-weight-black error--text text-subtitle-2 mb-0">
                      {{ row.qty | number('0,0.00') }} / {{ row.critic | number('0,0.00') }}
                    </div>
                    <div class="caption font-weight-bold grey--text">
                      Faltan {{ row.gap | number('0,0.00') }} {{ row.unit }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Lista 2: Mayor Capital Inmovilizado -->
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Mayor Capital Inmovilizado</div>
                <span class="caption grey--text font-weight-bold">SKU de alto valor</span>
              </div>
              <div class="panel-sub mb-4">Artículos con mayor monto financiero en bodega</div>

              <div v-if="!topValue.length" class="empty-mini py-6 text-center">Sin valorización</div>
              <div
                v-for="row in topValue"
                :key="'v-' + row.id"
                class="item-card-row mb-2 pa-3 rounded-lg is-clickable"
                @click="openArticle(row.id)"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="item-code font-weight-bold text-truncate primary--text" style="max-width: 260px">
                      {{ row.sku || ('ID ' + row.id) }}
                    </div>
                    <div class="caption grey--text text-truncate" style="max-width: 260px">
                      {{ row.name }} · {{ row.qty | number('0,0.00') }} {{ row.unit }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-weight-black primary--text text-subtitle-2 mb-0">
                      {{ row.value | moneda }}
                    </div>
                    <div class="caption grey--text">
                      {{ row.avg | moneda }} promedio
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Listas Operativas: Sin Movimiento & Órdenes de Compra Abiertas -->
        <v-row dense class="mb-6">
          <!-- Lista 3: Sin Movimiento Reciente -->
          <v-col cols="12" md="6">
            <v-card ref="slowCard" class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Sin Movimiento Reciente</div>
                <span class="caption grey--text font-weight-bold">Baja rotación</span>
              </div>
              <div class="panel-sub mb-4">Con stock físico pero sin compras en 90 días</div>

              <div v-if="!slow.length" class="empty-mini py-6 text-center">Sin SKU lentos en este filtro</div>
              <div
                v-for="row in slow"
                :key="'s-' + row.id"
                class="item-card-row mb-2 pa-3 rounded-lg is-clickable"
                @click="openArticle(row.id)"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="item-code font-weight-bold text-truncate" style="max-width: 260px">
                      {{ row.sku || ('ID ' + row.id) }}
                    </div>
                    <div class="caption grey--text text-truncate" style="max-width: 260px">
                      {{ row.name }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-weight-black text-subtitle-2 mb-0">
                      {{ row.qty | number('0,0.00') }} {{ row.unit }}
                    </div>
                    <div class="caption grey--text">
                      {{ lastBuyLabel(row.last_buy) }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Lista 4: Órdenes de Compra Abiertas -->
          <v-col cols="12" md="6">
            <v-card ref="posCard" class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title warning--text text--darken-3">
                  <v-icon x-small color="warning" class="mr-1">fas fa-truck-loading</v-icon>
                  Órdenes de Compra Abiertas
                </div>
                <span class="caption warning--text font-weight-bold">Pendiente de recepción</span>
              </div>
              <div class="panel-sub mb-4">Haz clic para abrir el detalle de la orden de compra</div>

              <div v-if="!purchaseOrders.length" class="empty-mini py-6 text-center">No hay OC ingresadas o aprobadas</div>
              <div
                v-for="row in purchaseOrders"
                :key="'po-' + row.id"
                class="item-card-row mb-2 pa-3 rounded-lg is-clickable"
                @click="$router.push({ name: 'CreatePurchaseOrder', params: { id: String(row.id) } })"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="item-code font-weight-bold text-truncate warning--text text--darken-3" style="max-width: 260px">
                      OC #{{ row.id }} · {{ row.estatus_label }}
                    </div>
                    <div class="caption grey--text text-truncate" style="max-width: 260px">
                      {{ row.provider || 'Sin proveedor' }} · {{ row.title }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-weight-black warning--text text--darken-4 text-subtitle-2 mb-0">
                      {{ row.value | moneda }}
                    </div>
                    <div class="caption grey--text">
                      {{ row.qty | number('0,0.0') }} un. pendientes
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla: Artículos sin existencia -->
        <v-card class="panel-card shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1">
            <div>
              <div class="panel-title">Artículos Sin Existencia</div>
              <div class="panel-sub">Activos en cero · Para reposición de stock o descontinuación</div>
            </div>
            <v-btn text small class="text-none font-weight-bold primary--text" @click="$router.push({ name: 'Stock', query: { alert: 'false' } })">
              Ver en inventario →
            </v-btn>
          </div>

          <div v-if="!zero.length" class="empty-mini py-6 text-center">No hay artículos activos en cero</div>
          <v-simple-table v-else class="zero-table mt-4">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Artículo</th>
                <th>Familia</th>
                <th class="text-end">Crítico</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in zero" :key="'z-' + row.id" class="click-row" @click="openArticle(row.id)">
                <td class="font-weight-black primary--text">{{ row.sku }}</td>
                <td class="font-weight-medium">{{ row.name }}</td>
                <td class="grey--text">{{ row.family || '—' }}</td>
                <td class="text-end font-weight-bold">{{ criticLabel(row.critic) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import { fetchInventoryDashboard } from '@/api/stocks'
import { generateInventoryDashboardPdf } from '@/doc_generators/inventoryDashboardReport'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name: 'InventoryDashboard',
  data() {
    return {
      loading: true,
      ready: false,
      requesting: false,
      downloadingPdf: false,
      error: '',
      store: null,
      family: null,
      days: 30,
      dayPresets: [
        { label: '7 días', value: 7 },
        { label: '30 días', value: 30 },
        { label: '90 días', value: 90 }
      ],
      lastQuery: '',
      asOf: '',
      kpis: {
        articles: 0,
        with_stock: 0,
        zero_stock: 0,
        critical: 0,
        inactive: 0,
        value: 0,
        qty: 0,
        open_supplies: 0,
        pending_pos: 0,
        pending_po_value: 0
      },
      movement: {
        in_count: 0,
        out_count: 0,
        in_qty: 0,
        out_qty: 0,
        in_value: 0,
        out_value: 0
      },
      stores: [],
      families: [],
      topValue: [],
      critical: [],
      zero: [],
      slow: [],
      daily: [],
      tipos: [],
      purchaseOrders: [],
      insight: null,
      storeOptions: [],
      familyOptions: []
    }
  },
  computed: {
    asOfLabel() {
      return this.asOf ? moment(this.asOf).format('DD/MM/YYYY') : '—'
    },
    maxStoreValue() {
      return this.maxOf(this.stores, 'value')
    },
    maxFamilyValue() {
      return this.maxOf(this.families, 'value')
    },
    maxTipoValue() {
      return this.maxOf(this.tipos, 'value')
    },
    maxDayQty() {
      let max = 1
      this.daily.forEach((row) => {
        const qty = Math.max(Number(row.in_qty || 0), Number(row.out_qty || 0))
        if (qty > max) max = qty
      })
      return max
    },
    insightChipColor() {
      const sev = this.insight?.severity || this.insight?.type
      if (sev === 'error' || sev === 'critical') return 'error'
      if (sev === 'warning' || sev === 'slow') return 'warning darken-1'
      if (sev === 'info' || sev === 'in_transit') return 'primary'
      return 'success'
    },
    insightChipLabel() {
      const t = this.insight?.type
      if (t === 'critical') return 'Riesgo Operativo'
      if (t === 'slow') return 'Atención de Capital'
      if (t === 'in_transit') return 'Abastecimiento en Tránsito'
      if (t === 'empty') return 'Catálogo Vacío'
      return 'Inventario en Equilibrio'
    },
    insightIcon() {
      const t = this.insight?.type
      if (t === 'critical') return 'fas fa-exclamation-triangle'
      if (t === 'slow') return 'fas fa-boxes'
      if (t === 'in_transit') return 'fas fa-truck-loading'
      if (t === 'empty') return 'fas fa-info-circle'
      return 'fas fa-shield-alt'
    },
    insightActionIcon() {
      const t = this.insight?.type
      if (t === 'critical' || t === 'slow' || t === 'in_transit') return 'fas fa-arrow-down'
      return 'fas fa-arrow-right'
    },
    insightButtonColor() {
      const sev = this.insight?.severity || this.insight?.type
      if (sev === 'error' || sev === 'critical') return 'error darken-1 white--text'
      if (sev === 'warning' || sev === 'slow') return 'warning darken-2 white--text'
      if (sev === 'info' || sev === 'in_transit') return 'primary white--text'
      return 'teal darken-2 white--text'
    }
  },
  methods: {
    maxOf(rows, key) {
      let max = 1
      ;(rows || []).forEach((row) => {
        const value = Number((row && row[key]) || 0)
        if (value > max) max = value
      })
      return max
    },
    sameFilter(a, b) {
      return String(a || '') === String(b || '')
    },
    rankWidth(value, max) {
      return Math.max(8, Math.round((Number(value || 0) / Math.max(1, max)) * 100)) + '%'
    },
    barHeight(value) {
      if (!value) return '4px'
      return Math.max(6, Math.round((Number(value) / this.maxDayQty) * 48)) + 'px'
    },
    lastBuyLabel(value) {
      if (!value) return 'Sin compra registrada'
      return 'Última compra ' + moment(value).format('DD/MM/YYYY')
    },
    criticLabel(value) {
      if (!value) return '—'
      return Number(value).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    openArticle(id) {
      this.$router.push({ name: 'DetailedArticle', params: { id } })
    },
    currentQuery() {
      return `days=${Number(this.days) || 30}&store=${this.store || ''}&family=${this.family || ''}`
    },
    async load(force) {
      const query = this.currentQuery()
      if (this.requesting) return
      if (!force && this.lastQuery === query && this.asOf) return
      this.requesting = true
      this.loading = true
      this.error = ''
      try {
        const data = await fetchInventoryDashboard(query)
        if (!data || !data.as_of) {
          throw new Error('Respuesta vacía del dashboard')
        }
        this.asOf = data.as_of
        this.lastQuery = query
        this.kpis = Object.assign({}, this.kpis, data.kpis || {})
        this.movement = Object.assign({}, this.movement, data.movement || {})
        this.stores = data.stores || []
        this.families = data.families || []
        this.topValue = data.top_value || []
        this.critical = data.critical || []
        this.zero = data.zero || []
        this.slow = data.slow || []
        this.daily = data.daily || []
        this.tipos = data.tipos || []
        this.purchaseOrders = data.purchase_orders || []
        this.insight = data.insight || null
        if (data.filters) {
          if (!this.storeOptions.length) this.storeOptions = data.filters.stores || []
          if (!this.familyOptions.length) this.familyOptions = data.filters.families || []
        }
        this.ready = true
      } catch (err) {
        this.error = 'No se pudo cargar el dashboard de inventarios.'
        notifications.showError('Error cargando inventario: ' + err)
      } finally {
        this.requesting = false
        this.loading = false
      }
    },
    async downloadExecutivePdf() {
      if (this.downloadingPdf) return
      this.downloadingPdf = true
      try {
        const company = this.$store.getters.getCompany || {}
        const selectedStore = this.storeOptions.find((s) => String(s.id) === String(this.store))
        const selectedFamily = this.familyOptions.find((f) => String(f.id) === String(this.family))

        await generateInventoryDashboardPdf({
          company,
          days: this.days,
          storeLabel: selectedStore ? selectedStore.name : 'Todas las bodegas',
          familyLabel: selectedFamily ? selectedFamily.name : 'Todas las familias',
          asOf: this.asOf,
          serverTimezone: company.current_time_zone || 'America/Santiago',
          kpis: this.kpis,
          movement: this.movement,
          stores: this.stores,
          families: this.families,
          critical: this.critical,
          topValue: this.topValue,
          purchaseOrders: this.purchaseOrders,
          zero: this.zero,
          insight: this.insight
        })
        notifications.showSuccess('Reporte PDF de inventarios generado correctamente.')
      } catch (err) {
        notifications.showError('No se pudo generar el reporte PDF: ' + (err.message || err))
      } finally {
        this.downloadingPdf = false
      }
    },
    triggerInsightAction() {
      if (!this.insight) return
      const type = this.insight.action_type || this.insight.type
      if (type === 'view_critical' || type === 'critical') {
        const el = this.$refs.criticalCard?.$el || this.$refs.criticalCard
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } else {
          this.$router.push({ name: 'Stock', query: { alert: 'true' } })
        }
      } else if (type === 'view_slow' || type === 'slow') {
        const el = this.$refs.slowCard?.$el || this.$refs.slowCard
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      } else if (type === 'view_pos' || type === 'in_transit') {
        const el = this.$refs.posCard?.$el || this.$refs.posCard
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      } else if (type === 'view_articles' || type === 'empty') {
        this.$router.push({ name: 'Stock' })
      } else {
        this.$router.push({ name: 'Stock' })
      }
    }
  },
  watch: {
    store(val, oldVal) {
      if (!this.ready || this.sameFilter(val, oldVal)) return
      this.load()
    },
    family(val, oldVal) {
      if (!this.ready || this.sameFilter(val, oldVal)) return
      this.load()
    },
    days(val, oldVal) {
      if (!this.ready || Number(val) === Number(oldVal)) return
      this.load()
    }
  },
  mounted() {
    this.load(true)
  }
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.border-bottom {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02) !important;
}

.tracking-tight {
  letter-spacing: -0.02em;
}

/* Banner Ejecutivo de Diagnóstico / Insight */
.insight-executive-card {
  background: #0f172a !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #ffffff;
}

.insight-executive-card.is-error {
  border-color: rgba(239, 68, 68, 0.35) !important;
  background: linear-gradient(135deg, #1e1b2e 0%, #0f172a 100%) !important;
}

.insight-executive-card.is-warning {
  border-color: rgba(245, 158, 11, 0.35) !important;
  background: linear-gradient(135deg, #241d13 0%, #0f172a 100%) !important;
}

.insight-executive-card.is-info {
  border-color: rgba(2, 132, 199, 0.35) !important;
  background: linear-gradient(135deg, #112233 0%, #0f172a 100%) !important;
}

.insight-executive-card.is-success {
  border-color: rgba(16, 185, 129, 0.35) !important;
  background: linear-gradient(135deg, #10261f 0%, #0f172a 100%) !important;
}

/* Tarjetas Héroe de KPIs (Idénticas a CommercialDashboard) */
.hero-kpi-card {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02);
}

.hero-kpi-card:hover {
  border-color: rgba(15, 23, 42, 0.15);
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 2px 4px -1px rgba(15, 23, 42, 0.03);
}

.hero-kpi-card.is-primary {
  border-color: #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%);
}

.hero-kpi-card.is-warning-card {
  border-color: #fecdd3;
  background: linear-gradient(180deg, #ffffff 0%, #fff1f2 100%);
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.kpi-value {
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.unit-text {
  font-size: 0.95rem;
  font-weight: 700;
  color: #64748b;
}

.kpi-footer {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

/* Paneles de Contenido */
.panel-card {
  background: #ffffff !important;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  border-radius: 16px !important;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
}

.panel-sub {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

/* Tarjetas de Flujo (In/Out) */
.flow-card {
  border-radius: 14px !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.flow-card:hover {
  transform: translateY(-2px);
}

.flow-card.is-in {
  background-color: #f0fdf4 !important;
  border-color: #dcfce7 !important;
}

.flow-card.is-out {
  background-color: #fff1f2 !important;
  border-color: #fecdd3 !important;
}

.flow-card.is-cyan {
  background-color: #f0f9ff !important;
  border-color: #e0f2fe !important;
}

.flow-card.is-amber {
  background-color: #fefce8 !important;
  border-color: #fef08a !important;
}

/* Rankings */
.rank-row {
  margin-bottom: 12px;
}

.rank-row:last-child {
  margin-bottom: 0;
}

.rank-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.rank-track {
  height: 6px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
}

.rank-fill {
  height: 100%;
  background: #00a650;
  border-radius: 4px;
}

.rank-fill.is-teal {
  background: #0284c7;
}

.rank-val {
  font-size: 12.5px;
  font-weight: 800;
  color: #0f172a;
}

/* Fila de Items Operativos */
.item-card-row {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  transition: all 0.15s ease;
}

.item-card-row:hover {
  border-color: #0284c7;
  background: #f8fafc;
}

.item-code {
  font-size: 13px;
}

.is-clickable {
  cursor: pointer;
}

/* Ticker Diario */
.day-strip {
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.06);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(28px, 1fr));
  gap: 4px;
  overflow-x: auto;
}

.day-cell {
  min-width: 28px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.day-name {
  font-size: 9px;
  font-weight: 800;
  color: #94a3b8;
}

.day-bar-track {
  height: 52px;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  margin: 4px 0 2px;
}

.day-bar {
  width: 7px;
  border-radius: 3px;
  min-height: 4px;
}

.day-bar.in {
  background: #00a650;
}

.day-bar.out {
  background: #ef4444;
}

.day-cell.is-empty .day-bar {
  background: #e2e8f0;
}

.day-count {
  font-size: 10px;
  font-weight: 800;
  color: #0f172a;
}

.empty-mini {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}

.compact-select >>> fieldset {
  border-radius: 10px !important;
  border-color: rgba(15, 23, 42, 0.12) !important;
}

.zero-table >>> th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b !important;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
}

.zero-table >>> td {
  border-bottom: 1px solid rgba(15, 23, 42, 0.04) !important;
}

.click-row {
  cursor: pointer;
}

.click-row:hover {
  background: #f8fafc;
}
</style>
